import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import I18n from 'PMT/i18n/I18n';

// -- constants
import Events from 'constants/Events';
import RoutingEnum from 'constants/RoutingEnum';

// -- utils
import ViewUtils from 'PMT/utils/ViewUtils';
import Listener from 'PMT/flux/Listener';
import SecurityContext from 'utils/permissions/SecurityContext';

// -- actions
import { RestaurantMenuAction, RouteAction } from 'actions';

// -- stores
import SessionStore from 'stores/SessionStore';
import {RestaurantMenuStore, OrderSettingsStore, RestaurantStore} from 'stores';

// -- entities
import Restaurant from 'PMT/entities/Restaurant';
import { ProductOption, RestaurantMenu } from 'PMT/entities/menu';

import { OrderSettings } from 'PMT/entities/order';

// -- components
import RestaurantMenuTabs from "pages/menu/RestaurantMenuTabs";
import AbstractComponent from 'components/AbstractComponent';
import Ariane from 'PMT/components/Ariane';
import ProductOptionEditView from './ProductOptionEditView';

export default class ProductOptionEditPage extends AbstractComponent {
  state = {
    restaurantMenu: RestaurantMenuStore.getRestaurantMenu(),
    restaurant: RestaurantStore.getRestaurant(),
    productOption: this.isEditMode() ? null : new ProductOption(),
    apiError: null,
  };

  componentDidMount() {
    RestaurantMenuStore.addListeners(this.restaurantMenuStoreListeners);

    if (!SecurityContext.restaurantMenu.canEdit()) {
      // TODO: redirect
    }

    if (_.isNull(this.state.restaurantMenu)) {
      RestaurantMenuAction.loadRestaurantMenu();
    } else if (this.isEditMode()) {
      this.loadProductOption();
    }
  }

  componentWillUnmount() {
    RestaurantMenuStore.removeListeners(this.restaurantMenuStoreListeners);
  }

  get restaurantMenuStoreListeners(): Array<Listener> {
    return [
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_SUCCESS, this.onChange),
      new Listener(Events.ON_LOADING_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_SUCCESS,
        this.onProductOptionUpdated
      ),
      new Listener(Events.ON_UPDATE_RESTAURANT_MENU_ERROR, this.onError),

      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_PRODUCT_OPTION_SUCCESS,
        this.onProductOptionUpdated
      ),
      new Listener(
        Events.ON_UPDATE_RESTAURANT_MENU_PRODUCT_OPTION_ERROR,
        this.onError
      ),

      new Listener(
        Events.ON_CREATE_RESTAURANT_MENU_PRODUCT_OPTION_SUCCESS,
        this.onProductOptionCreated
      ),
      new Listener(
        Events.ON_CREATE_RESTAURANT_MENU_PRODUCT_OPTION_ERROR,
        this.onError
      ),

      new Listener(
        Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_OPTION_SUCCESS,
        this.onProductOptionDeleted
      ),
      new Listener(
        Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_OPTION_ERROR,
        this.onError
      ),

      new Listener(
        Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_OPTION_IMAGE_SUCCESS,
        this.onProductOptionUpdated
      ),
      new Listener(
        Events.ON_DELETE_RESTAURANT_MENU_PRODUCT_OPTION_IMAGE_ERROR,
        this.onError
      ),
    ];
  }

  loadProductOption() {
    const productOption: ProductOption = this.getProductOptionOnRestaurantMenu(
      this.state.restaurantMenu
    );

    // TODO: if productOption === null redirect to 404.
    this.setState({
      productOption,
      loading: false,
    });
  }

  // -- Event functions

  onChange = () => {
    const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();

    if (this.isEditMode()) {
      const productOption: ProductOption = this.getProductOptionOnRestaurantMenu(
        restaurantMenu
      );
      this.setState({
        restaurantMenu,
        productOption,
        loading: false,
      });
    } else {
      this.setState({
        restaurantMenu,
        loading: false,
      });
    }
  };

  onProductOptionUpdated = () => {
    if (this.isEditMode()) {
      const restaurantMenu: RestaurantMenu = RestaurantMenuStore.getRestaurantMenu();
      const productOption: ProductOption = this.getProductOptionOnRestaurantMenu(
        restaurantMenu
      );

      this.setState({
        restaurantMenu,
        productOption,
        loading: false,
      });
    }
  };

  onProductOptionCreated = () => {
    RouteAction.presentProductOptionDetail(
      RestaurantMenuStore.getCreatedProductOption()
    );
  };

  onProductOptionDeleted = () => {
    RouteAction.presentOptionsTab();
  };

  onError = () => {
    this.setState({
      apiError: RestaurantMenuStore.getApiError(),
    });
  };

  onUpdateProductOption = (productOption: ProductOption, image: ?DataUri) => {
    this.showLoading();
    const imageFile = _.isNull(image) ? null : ViewUtils.dataUriToFile(image);
    RestaurantMenuAction.saveProductOption(productOption, imageFile);
  };

  onDeleteProductOptionImage = (productOption: ProductOption) => {
    this.showLoading();
    RestaurantMenuAction.deleteProductOptionImage(productOption);
  };

  onCreateProductOption = (productOption: ProductOption, image: ?DataUri) => {
    this.showLoading();
    const imageFile = _.isNull(image) ? null : ViewUtils.dataUriToFile(image);
    RestaurantMenuAction.createProductOption(productOption, imageFile);
  };

  onDeleteProductOption = (productOption: ProductOption) => {
    this.showLoading();
    RestaurantMenuAction.deleteProductOption(productOption);
  };

  get productOption(): ProductOption {
    return this.state.productOption;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.state.restaurantMenu;
  }

  get restaurant(): Restaurant {
    return this.state.restaurant;
  }


  isEditMode() {
    return !_.isUndefined(this.props.match.params.productOptionId);
  }

  getProductOptionOnRestaurantMenu(restaurantMenu) {
    const productOptionId = this.props.match.params.productOptionId;
    const productOption: ProductOption = restaurantMenu.getProductOptionWithId(
      productOptionId
    );

    return productOption;
  }

  // -- renderers

  renderBody() {
    if (!_.isNull(this.apiError)) {
      return this.renderApiErrorBlock();
    } else if (_.isNull(this.productOption)) {
      return this.renderLoading(true);
    }

    const arianeIcon = 'fa-shopping-cart';

    const arianeDetails = [
      {
        name: I18n.tr('menu.order'),
        link: null,
      },
      {
        name: I18n.tr('menu.order.restaurant_menu'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuTabs.CATEGORIES,
        },
      },
      {
        name: I18n.tr('menu.order.restaurant_menu.options'),
        link: RoutingEnum.RESTAURANT_MENU,
        query: {
          tab: RestaurantMenuTabs.OPTIONS,
        },
      },
    ];

    if (this.isEditMode()) {
      arianeDetails.push({
        name: this.productOption.name,
        link: RoutingEnum.RESTAURANT_MENU_PRODUCT_OPTION_DETAIL,
        params: {
          productOptionId: this.productOption.id,
        },
      });
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.edition'),
        link: null,
      });
    } else {
      arianeDetails.push({
        name: I18n.tr('menu.order.restaurant_menu.new'),
        link: null,
      });
    }

    return (
      <div>
        <Ariane icon={arianeIcon} details={arianeDetails} />

        <ProductOptionEditView
          productOption={this.productOption}
          restaurantMenu={this.restaurantMenu}
          onUpdate={this.onUpdateProductOption}
          onCreate={this.onCreateProductOption}
          onDelete={this.onDeleteProductOption}
          onDeleteImage={() =>
            this.onDeleteProductOptionImage(this.productOption)
          }
          isEditMode={this.isEditMode()}
          orderSettings={OrderSettingsStore.getOrderSettings()}
          currencyCode={this.restaurant.currency}
        />
      </div>
    );
  }

  render() {
    try {
      return (
        <div className="productOption_detail_page">
          {this.renderBody()}
          {this.renderLoading()}
        </div>
      );
    } catch (e) {
      console.error(e);
    }
  }
}
